import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  getAuth,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { useForm } from "@mantine/form";
import { Button, PasswordInput } from "@mantine/core";

import { PageLoader } from "@components/PageLoader/PageLoader";
import { FormWithImage } from "@components/FormWithImage/FormWithImage";

export const ResetPasswordRoute: FC = () => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingOobCode, setIsVerifyingOobCode] = useState(true);

  const navigate = useNavigate();

  const searchParams = new URLSearchParams(window.location.search);
  const oobCode = searchParams.get("oobCode");

  const { getInputProps, onSubmit, setFieldError } = useForm({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: {
      confirmPassword: (_, { password, confirmPassword }) =>
        password !== confirmPassword ? "Password does not match" : null,
    },
  });
  const handleSubmit = onSubmit(async ({ password }) => {
    if (!oobCode) return;
    setIsLoading(true);
    try {
      await confirmPasswordReset(getAuth(), oobCode, password);
      navigate("/login");
    } catch (ex) {
      setFieldError(
        "password",
        (ex as Error).message.replaceAll(/Firebase: | \(.*\)/gi, "")
      );
    }
    setIsLoading(false);
  });

  const $toRefs = { navigate };
  const toRefs = useRef($toRefs);
  useEffect(() => {
    toRefs.current = $toRefs;
  });

  useEffect(() => {
    if (!oobCode) return toRefs.current.navigate("/forgot-password");
    verifyPasswordResetCode(getAuth(), oobCode)
      .then(() => {
        setIsVerifyingOobCode(false);
      })
      .catch(() => {
        toRefs.current.navigate("/forgot-password");
      });
  }, [oobCode]);

  if (isVerifyingOobCode) return <PageLoader />;

  return (
    <FormWithImage
      title={t("setNewPassword.header")}
      description=""
      onSubmit={handleSubmit}
    >
      <PasswordInput
        required
        autoComplete="new-password"
        label={t("Password")}
        {...getInputProps("password")}
      />
      <PasswordInput
        required
        autoComplete="new-password"
        label={t("Confirm Password")}
        {...getInputProps("confirmPassword")}
      />
      <Button type="submit" loading={isLoading}>
        Reset Password
      </Button>
    </FormWithImage>
  );
};
